import { Suspense, lazy, useEffect, useState } from 'react'
import { useGameSfx } from '../audio/useGameSfx'
import { BOARD } from '../game/board'
import { useGame } from '../store/gameStore'
import type { GameState } from '../game/types'
import { ActionBar } from './ActionBar'
import { EventLog } from './EventLog'
import { PlayerStrip } from './PlayerStrip'
import { PromptModal } from './PromptModal'
import { Rules } from './Rules'
import { StatementPanel } from './StatementPanel'
import { TileInfo } from './TileInfo'
import { WinScreen } from './WinScreen'

type SideTab = 'statement' | 'log'

// three.js หนักราว 140KB gzip โหลดตอนเข้าเกมจริงเท่านั้น หน้าตั้งค่า/ล็อบบี้จะได้เปิดไว
const Board3D = lazy(() => import('./Board3D').then((m) => ({ default: m.Board3D })))

export function GameScreen({ g }: { g: GameState }) {
  const { inspecting, inspect, showRules } = useGame()
  const mySeat = useGame((s) => (s.mode === 'online' ? s.net.seatId : null))
  const [tab, setTab] = useState<SideTab>('statement')
  const [tileId, setTileId] = useState<number | null>(null)
  /** โหมดจอเล็ก: แผงข้อมูลเลื่อนขึ้นจากขอบล่าง ปกติซ่อนไว้ให้กระดานเต็มจอ */
  const [sheet, setSheet] = useState(false)

  useGameSfx(g, mySeat)

  // เมื่อเปลี่ยนตา ให้แผงงบการเงินตามไปที่ผู้เล่นคนปัจจุบันเสมอ
  useEffect(() => {
    inspect(null)
    setTileId(null)
  }, [g.current, inspect])

  const shown = g.players[inspecting ?? g.current]

  return (
    <div className="game">
      <div className="game-main">
        <Suspense
          fallback={
            <div className="board-wrap">
              <div className="board-loading">
                <span className="spinner" aria-hidden />
                <span>กำลังเปิดกระดาน 3 มิติ…</span>
              </div>
            </div>
          }
        >
          <Board3D
            g={g}
            onTile={(id) => {
              setTileId((prev) => (prev === id ? null : id))
              const owner = g.tiles[id].ownerId
              if (owner !== null && BOARD[id].price !== undefined) {
                inspect(owner)
                setTab('statement')
              }
            }}
          />
        </Suspense>
        <ActionBar g={g} />
      </div>

      <aside className="game-side">
        {/*
          ฉากหลังต้องอยู่ "ใน" .game-side ไม่ใช่ข้างนอก
          เพราะ .game-side เป็น position:fixed ซึ่ง Chrome ถือว่าสร้าง stacking context
          เองเสมอแม้ไม่ได้ใส่ z-index — แผ่นข้อมูลที่อยู่ข้างในจึงไม่มีทางขึ้นมาทับ
          ฉากหลังที่อยู่ข้างนอกได้ ผลคือแผ่นถูกเบลอและแตะไม่โดน
        */}
        {sheet && <div className="sheet-scrim" onClick={() => setSheet(false)} />}
        {tileId !== null && <TileInfo g={g} tileId={tileId} onClose={() => setTileId(null)} />}
        <PlayerStrip g={g} inspecting={inspecting} onSelect={(id) => inspect(id === inspecting ? null : id)} />

        {/*
          บนจอเล็กส่วนนี้กลายเป็นแผ่นเลื่อนขึ้นจากขอบล่าง กระดานจะได้เต็มจอ
          บนเดสก์ท็อป .side-sheet ตั้ง display:contents ลูก ๆ จึงเรียงใน aside
          เหมือนเดิมทุกประการ ไม่ต้องแยกโครง JSX สองชุด
        */}
        <div className={`side-sheet${sheet ? ' open' : ''}`}>
          <button className="sheet-grip" onClick={() => setSheet(false)} aria-label="ปิดแผงข้อมูล">
            <span />
          </button>

          <div className="side-tabs">
            <button className={tab === 'statement' ? 'on' : ''} onClick={() => setTab('statement')}>
              📊 งบการเงิน
              {inspecting !== null && inspecting !== g.current && (
                <span className="pill">{g.players[inspecting].character.emoji}</span>
              )}
            </button>
            <button className={tab === 'log' ? 'on' : ''} onClick={() => setTab('log')}>
              📜 บันทึก
            </button>
          </div>

          <div className="side-body">
            {tab === 'statement' ? (
              <div className="card statement-card">
                <StatementPanel g={g} player={shown} />
              </div>
            ) : (
              <EventLog g={g} />
            )}
          </div>
        </div>
      </aside>

      {/* ปุ่มลอยเปิดแผงข้อมูล — โผล่เฉพาะโหมดจอเล็ก (ดู CSS) */}
      <button className="sheet-toggle" onClick={() => setSheet((v) => !v)} aria-expanded={sheet}>
        {sheet ? '✕' : '📊'}
      </button>

      {g.phase === 'gameover' ? <WinScreen g={g} /> : <PromptModal g={g} />}
      {showRules && <Rules />}
    </div>
  )
}
